import type { AdminSettings } from '@diora/contracts';
import { useQuery } from '@tanstack/react-query';

import { apiGet } from '@/lib/api';
import { qk } from '@/lib/query';

/**
 * Site settings, cached for the session.
 *
 * Every screen that shows a price needs the currency, and it changes about once
 * in the life of the restaurant — so this is read once and shared rather than
 * threaded through props from the top.
 *
 * `currency` being null is a real state, not a loading one: until the owner
 * picks one the API refuses to publish anything priced, and a price still
 * renders as a bare number.
 */
export function useSettings() {
  const query = useQuery({
    queryKey: qk.settings,
    queryFn: () => apiGet<AdminSettings>('/admin/settings'),
    staleTime: 5 * 60_000,
  });

  return {
    settings: query.data?.data ?? null,
    currency: query.data?.data?.regional.currencyCode ?? null,
    defaultTimezone: query.data?.data?.regional.defaultTimezone ?? null,
    isLoading: query.isLoading,
  };
}
